<template>
  <div>
    <div>
      <aha-modal @hide="hide()" :title="canUseTrashBin ? headingTitleV2 : headingTitle">
        <div v-if="deletedPresentations.length === 1">
          <div>
            <span v-html="canUseTrashBin ? bodyTextContentV2 : bodyTextContent"></span>
            <span v-if="!canUseTrashBin" class="font-bold -mr-0.5">
              {{ deletedPresentations[0].name }} [{{ deletedPresentations[0].accessCode }}]
            </span>
            <span v-if="!canUseTrashBin">{{ questionMark }}</span>
          </div>
        </div>
        <div class="max-h-52" v-else>
          <span v-html="canUseTrashBin ? bodyTextContentV2 : bodyTextContent"></span>
          <ul class="my-2 ml-4 list-disc">
            <li
              class="mt-1"
              :key="presentation.id"
              v-for="presentation in deletedPresentations"
            >
              {{ presentation.name }} [{{ presentation.accessCode }}]
            </li>
          </ul>
          <span v-if="canUseTrashBin">{{ bottomTextContent }}</span>
        </div>

        <template #action>
          <aha-button
            variant="danger"
            :disabled="deletingPresentations"
            @click="handleDeletePresentations()"
            icon-name="delete"
            :submitting="deletingPresentations"
            has-margin-right
          >
            <aha-spinner variant="white" class="mr-1" v-if="deletingPresentations" />
            {{ canUseTrashBin ? $i18nPresenter.t("Move to trash") : $i18nPresenter.t("Delete") }}
          </aha-button>

          <aha-link @click="hide()">
            <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
          </aha-link>
        </template>
      </aha-modal>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { get } from 'lodash'

// api
import PresentationApi from '@/api/presentation'

// constants
import eventCategories from '@/constant/eventCategories'

// utils
import utils from '@/util'

// eventbus
import EventBus from '@/eventBus'

const PARTICIPANTS_COUNT = 4

export default {
  props: {
    deletedPresentations: {
      type: Array,
      default: () => [],
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      deletingPresentations: false,
    }
  },
  computed: {
    ...mapState({
      presentations: (state) => state.presentations,
    }),
    ...mapGetters('featureToggles', ['canUseTrashBin']),
    isSingleDelete() {
      return this.deletedPresentations.length === 1
    },
    totalParticipants() {
      if (!this.deletedPresentations || !this.deletedPresentations.length) return 0

      return this.deletedPresentations.reduce((acc, presentation) => {
        return acc + presentation.participantsCount
      }, 0)
    },
    hasPresentations() {
      return this.deletedPresentations && this.deletedPresentations.length
    },
    headingTitleV2() {
      if (this.isSingleDelete) {
        const objectName = get(this, 'deletedPresentations[0].name', '')
        return this.$i18nPresenter.t('Delete {objectName}?', { objectName })
      }

      return this.$i18nPresenter.t('Delete multiple presentations?')
    },
    headingTitle() {
      if (!this.hasPresentations) {
        return this.$i18nPresenter.t('Delete confirmation')
      }

      return this.totalParticipants > PARTICIPANTS_COUNT
        ? this.$i18nPresenter.t('are_you_sure_you_want_to_delete')
        : this.$i18nPresenter.t('Delete confirmation')
    },
    bodyTextContentV2() {
      if (this.isSingleDelete) {
        const objectName = get(this, 'deletedPresentations[0].name', '')
        return this.$i18nPresenter.t('You can restore {objectName} from the Trash later.', {
          objectName,
        })
      }

      return this.$i18nPresenter.t('You are about to delete the following presentations:')
    },
    bottomTextContent() {
      if (this.canUseTrashBin) {
        return this.$i18nPresenter.t('You can restore them from the Trash later.')
      }

      return ''
    },
    bodyTextContent() {
      if (!this.hasPresentations) return ''

      if (this.totalParticipants > PARTICIPANTS_COUNT) {
        return this.$i18nPresenter.t('audience_wont_be_see_presentation_anymore')
      }

      return this.deletedPresentations.length === 1
        ? this.$i18nPresenter.t('Are you sure you want to delete this presentation:')
        : this.$i18nPresenter.t('Are you sure you want to delete these presentations:')
    },
    questionMark() {
      return this.totalParticipants > PARTICIPANTS_COUNT ? '' : '?'
    },
  },
  methods: {
    ...mapActions(['setPresentationsAction', 'setErrorOnInternalServerAction']),
    async handleDeletePresentations() {
      this.deletingPresentations = true
      const deletedPresentationIds = this.deletedPresentations
        .map((presentation) => presentation.id)

      try {
        await PresentationApi.deletePresentations(deletedPresentationIds)

        if (this.deletedPresentations.length === 1) {
          utils.showToastSuccess(this.$i18nPresenter.t('Moved {objectName} to Trash', { objectName: this.deletedPresentations[0].name }))
        } else {
          utils.showToastSuccess(this.$i18nPresenter.t('Moved {number} presentations to Trash', { number: this.deletedPresentations.length }))
        }

        EventBus.$emit('reload-presentations-and-folders')

        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation > Delete > Succeeded')
      } catch (error) {
        if (this.deletedPresentations.length === 1 && this.deletedPresentations[0]) {
          this.setErrorOnInternalServerAction(`Cannot delete presentation ${utils.sanitize(this.deletedPresentations[0].name)}`)
        } else {
          this.setErrorOnInternalServerAction(`Cannot delete ${this.deletedPresentations.length} presentations`)
        }

        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation > Delete > Failed')
        throw error
      } finally {
        this.deletingPresentations = false
        this.hide()
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>
